import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAppSettings } from "@/context/AppSettingsContext";
import { useInvoiceSettings, usePrinterSettings } from "@/hooks/usePosConfig";
import { fetchInvoiceDetail } from "@/lib/salesApi";
import type { InvoicePrintData } from "@/lib/invoiceTemplate";

interface PreviewResult {
  data: InvoicePrintData | null;
  loading: boolean;
  error: string | null;
  printer: ReturnType<typeof usePrinterSettings>;
}

/** Loads everything needed to render/print an invoice (items, payments, customer, branch, cashier). */
export function useInvoicePreview(invoiceId: string | null): PreviewResult {
  const { settings, posSettings, a4Settings } = useInvoiceSettings();
  const printer = usePrinterSettings();
  const { settings: appSettings, fiscal } = useAppSettings();
  const [data, setData] = useState<InvoicePrintData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!invoiceId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const detail = await fetchInvoiceDetail(invoiceId);
      if (!detail) {
        setError("No se pudo cargar la factura.");
        return;
      }
      const { invoice } = detail;

      const [customerRes, branchRes, cashierRes] = await Promise.all([
        invoice.customer_id
          ? supabase
              .from("customers")
              .select("first_name, last_name, document, phone, address")
              .eq("id", invoice.customer_id)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        invoice.branch_id
          ? supabase
              .from("branches")
              .select("name, address, phone, rnc")
              .eq("id", invoice.branch_id)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        invoice.issued_by
          ? supabase
              .from("profiles")
              .select("full_name, last_name")
              .eq("id", invoice.issued_by)
              .maybeSingle()
          : Promise.resolve({ data: null }),
      ]);

      const customerRow = customerRes.data as
        | {
            first_name: string;
            last_name: string;
            document: string | null;
            phone: string | null;
            address: string | null;
          }
        | null;
      const branchRow = branchRes.data as
        | { name: string; address: string | null; phone: string | null; rnc: string | null }
        | null;
      const cashierRow = cashierRes.data as { full_name: string; last_name: string } | null;

      setData({
        invoice: detail.invoice,
        items: detail.items,
        payments: detail.payments,
        company: {
          name: appSettings.company_name ?? "Sistema RKC",
          rnc: appSettings.rnc,
          address: appSettings.address,
          phone: appSettings.phone,
          email: appSettings.email,
          whatsapp: appSettings.whatsapp,
          logoUrl: appSettings.logo_url,
        },
        branch: branchRow,
        customer: customerRow
          ? {
              name: `${customerRow.first_name} ${customerRow.last_name}`.trim(),
              document: customerRow.document,
              phone: customerRow.phone,
              address: customerRow.address,
            }
          : null,
        cashier: cashierRow ? `${cashierRow.full_name} ${cashierRow.last_name}`.trim() : null,
        posSettings,
        a4Settings,
        fiscal: {
          enabled: fiscal.enabled ?? false,
          receiptType: fiscal.receipt_type ?? null,
          ncf: fiscal.ncf_prefix && fiscal.ncf_sequence ? `${fiscal.ncf_prefix}${fiscal.ncf_sequence}` : null,
        },
      });
    } catch {
      setError("No se pudo cargar la factura.");
    } finally {
      setLoading(false);
    }
  }, [invoiceId, appSettings, posSettings, a4Settings, fiscal]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, printer };
}